import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Boxes, Loader2, Search, User, Users } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../../shared/ui/icons/SubjectKindIcon";
import { Modal } from "../../../../shared/ui/modal";
import { useTherapist } from "../../../../context/useTherapist";
import { offeredCaseload, useCourseLabels, useSessionPreferences } from "../../../center";
import { type Patient, groupLabel, type GroupPatient } from "../../../patient";
import type { CouplePatient } from "../../../patient";
import { loadConstellationCaseload } from "../../application/constellationCaseload";
import { launchConstellation } from "../../application/constellationSessions";

type Target =
    | { kind: "individual"; id: string; label: string }
    | { kind: "couple"; id: string; label: string }
    | { kind: "group"; id: string; label: string };

type Props = { isOpen: boolean; onClose: () => void };

/** "Open board" chooser: start a board for an existing patient/couple, or open
 *  a free board with no linked patient (issue #197). */
export default function ConstellationOpenBoardModal({ isOpen, onClose }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { therapist } = useTherapist();
    // A training centre's student is named with the course they are on.
    const { labelOf } = useCourseLabels();
    // Couples and groups only where the centre runs them.
    const { modes } = useSessionPreferences();

    const [targets, setTargets] = useState<Target[]>([]);
    const [loadingList, setLoadingList] = useState(false);
    const [query, setQuery] = useState("");
    const [launching, setLaunching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen || !therapist?.id) return;
        setLoadingList(true);
        setError(null);
        loadConstellationCaseload(therapist.id)
            .then((caseload) => offeredCaseload(caseload, modes))
            .then(({ patients, couples: coupleList, groups: groupList }) => {
                const individuals: Target[] = (patients || []).map(
                    (p: Patient) => ({ kind: "individual", id: p.id, label: labelOf(p) }),
                );
                const couples: Target[] = (coupleList || []).map((c: CouplePatient) => ({
                    kind: "couple",
                    id: c.id,
                    label: c.display_name,
                }));
                const groups: Target[] = (groupList || []).map((g: GroupPatient) => ({
                    kind: "group",
                    id: g.id,
                    label: groupLabel(g),
                }));
                setTargets([...individuals, ...couples, ...groups]);
            })
            .catch(() => setError(t("constellation.launchSheet.loadError")))
            .finally(() => setLoadingList(false));
    }, [isOpen, therapist?.id, t, labelOf, modes]);

    useEffect(() => {
        if (!isOpen) {
            setQuery("");
            setError(null);
            setLaunching(false);
        }
    }, [isOpen]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return targets;
        return targets.filter((tgt) => tgt.label.toLowerCase().includes(q));
    }, [targets, query]);

    const launch = async (run: () => Promise<string>) => {
        if (launching) return;
        setLaunching(true);
        setError(null);
        try {
            const path = await run();
            onClose();
            navigate(path);
        } catch {
            setError(t("constellation.launchSheet.launchError"));
            setLaunching(false);
        }
    };

    const launchTarget = (tgt: Target) =>
        launch(async () => {
            const data =
                tgt.kind === "individual"
                    ? await launchConstellation("individual", tgt.id)
                    : tgt.kind === "group"
                      ? await launchConstellation("group", null, undefined, undefined, undefined, tgt.id)
                      : await launchConstellation("couple", null, tgt.id);
            return `/constellation/${data.board.id}`;
        });

    const launchFree = () =>
        launch(async () => {
            const data = await launchConstellation("anonymous", null);
            return `/constellation/${data.board.id}`;
        });

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="mx-4 max-w-lg rounded-3xl">
            <div className="flex max-h-[80vh] flex-col p-5">
                <h2 className="mb-1 text-lg font-bold text-gray-800 dark:text-white">
                    {t("constellation.launchSheet.anonymousTitle")}
                </h2>
                <p className="mb-4 text-sm text-content-tertiary">
                    {t("constellation.openBoard.subtitle")}
                </p>

                {/* Free board (no linked patient) */}
                <button
                    type="button"
                    onClick={launchFree}
                    disabled={launching}
                    className="mb-4 flex w-full items-center gap-3 rounded-2xl border border-brand-100 bg-brand-50 p-3 text-left text-brand-700 transition-colors hover:bg-brand-100 disabled:opacity-50 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300"
                >
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-brand-600 shadow-sm dark:bg-gray-800">
                        <Boxes className="h-5 w-5" />
                    </span>
                    <span className="flex-1">
                        <span className="block text-sm font-semibold">
                            {t("constellation.openBoard.freeTitle")}
                        </span>
                        <span className="block text-xs opacity-80">
                            {t("constellation.openBoard.freeHint")}
                        </span>
                    </span>
                </button>

                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-content-tertiary">
                    {t("constellation.launchSheet.existingLabel")}
                </div>

                {/* Search */}
                <div className="relative mb-3">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-content-tertiary" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t("constellation.launchSheet.searchPlaceholder")}
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-3 text-sm text-gray-800 outline-none focus:border-brand-300 focus:ring-2 focus:ring-brand-100 dark:border-gray-700 dark:bg-gray-800/50 dark:text-white"
                    />
                </div>

                {/* Results */}
                <div className="min-h-0 flex-1 overflow-y-auto">
                    {loadingList ? (
                        <div className="flex items-center justify-center py-8 text-content-tertiary">
                            <Loader2 className="h-5 w-5 animate-spin" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <p className="py-6 text-center text-sm text-content-tertiary">
                            {t("constellation.launchSheet.noResults")}
                        </p>
                    ) : (
                        <ul className="space-y-1">
                            {filtered.map((tgt) => (
                                <li key={`${tgt.kind}:${tgt.id}`}>
                                    <button
                                        type="button"
                                        onClick={() => launchTarget(tgt)}
                                        disabled={launching}
                                        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-gray-50 disabled:opacity-50 dark:hover:bg-gray-800/50"
                                    >
                                        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                                            {tgt.kind === "group" ? (
                                                <GroupOfPeopleIcon className="h-4 w-4" />
                                            ) : tgt.kind === "couple" ? (
                                                <Users className="h-4 w-4" />
                                            ) : (
                                                <User className="h-4 w-4" />
                                            )}
                                        </span>
                                        <span className="flex-1 truncate text-sm font-medium text-gray-800 dark:text-gray-100">
                                            {tgt.label}
                                        </span>
                                        <span className="text-xs font-semibold uppercase tracking-wide text-content-tertiary">
                                            {t(`common.${tgt.kind}`)}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {error && (
                    <p role="alert" className="mt-3 text-center text-sm text-red-500">
                        {error}
                    </p>
                )}

                <button
                    type="button"
                    onClick={onClose}
                    disabled={launching}
                    className="mt-4 w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800/50"
                >
                    {t("common.cancel")}
                </button>
            </div>
        </Modal>
    );
}
